'use client';

import { useEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

function getTimeLeft(target: Date): TimeLeft | null {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return null;
  const totalSecs = Math.floor(diff / 1000);
  const days    = Math.floor(totalSecs / 86400);
  const hours   = Math.floor((totalSecs % 86400) / 3600);
  const minutes = Math.floor((totalSecs % 3600) / 60);
  return { days, hours, minutes };
}

function Segment({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="bg-white/10 rounded-lg px-2.5 py-1 min-w-[3rem] text-center">
        <span className="text-2xl sm:text-3xl font-black text-orange-400 tabular-nums leading-none">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[9px] text-white/40 uppercase tracking-widest font-medium">{label}</span>
    </div>
  );
}

interface UTBKCountdownProps {
  /** ISO datetime — the Banner's `targetDate` field. */
  targetDate: string;
  /** The Banner's `title`, used as the countdown label. */
  label: string;
  ctaText?: string | null;
  ctaUrl?: string | null;
  bgColor?: string | null;
  bgGradient?: string | null;
  textColor?: string | null;
}

// The target date is resolved on the server and passed in, so the first paint is
// already correct. Fetching it client-side would render the strip, then rip it
// back out once a past target date came back — a visible flash under the banner.
export function UTBKCountdown({ targetDate, label, ctaText, ctaUrl, bgColor, bgGradient, textColor }: UTBKCountdownProps) {
  const t = useTranslations('utbkCountdown');
  // Starts null on purpose — see EventCountdown. Deriving the remaining time during
  // render diverges between the SSR pass and hydration and trips a mismatch.
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Tick every minute
  useEffect(() => {
    const tick = () => setTimeLeft(getTimeLeft(new Date(targetDate)));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (!timeLeft || dismissed) return null;

  const color = textColor ?? '#ffffff';
  const containerStyle: React.CSSProperties = bgGradient
    ? { background: bgGradient }
    : bgColor
    ? { backgroundColor: bgColor }
    : {};

  return (
    <div className="bg-slate-950 border-b border-white/5 relative overflow-hidden" style={containerStyle}>
      {/* Subtle dot texture */}
      <div
        className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}
      />

      <div className="relative max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        {/* Left: label + countdown */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Label — desktop only */}
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color, opacity: 0.6 }}>{t('hitungMundur')}</span>
            <span className="text-sm font-bold whitespace-nowrap" style={{ color }}>{label}</span>
          </div>
          {/* Label — mobile */}
          <span className="sm:hidden text-xs font-bold whitespace-nowrap" style={{ color }}>{label}</span>

          {/* Divider */}
          <div className="hidden sm:block w-px h-9 bg-white/10 flex-shrink-0" />

          {/* Countdown segments */}
          <div className="flex items-end gap-2">
            <Segment value={timeLeft.days}    label={t('hari')} />
            <span className="text-orange-400/50 font-black text-xl mb-5 leading-none select-none">:</span>
            <Segment value={timeLeft.hours}   label={t('jam')} />
            <span className="text-orange-400/50 font-black text-xl mb-5 leading-none select-none">:</span>
            <Segment value={timeLeft.minutes} label={t('menit')} />
          </div>
        </div>

        {/* Right: CTA + dismiss */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {ctaUrl && ctaText && (
            <a
              href={ctaUrl}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-slate-900 bg-orange-400 hover:bg-orange-300 px-3.5 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              {ctaText} <ArrowRight className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={t('tutup')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
